/* eslint-disable no-unused-vars */

import React, { useState, useRef, useCallback, useEffect } from "react";
import {
  XMarkIcon,
  PaperAirplaneIcon,
  TrashIcon,
} from "@heroicons/react/24/outline";
import AILoadingIndicator from "../ai/components/AILoadingIndicator";
import { useAIError } from "../ai/context/AIErrorContext";
import MarkdownRenderer from "./MarkdownRenderer";
import { useNotes } from "../context/NotesContext";
import { useError } from '../context/ErrorContext';
import ErrorState from './ErrorState';
import { apiService } from '../services/apiService';
import { useMediaQuery } from 'react-responsive';

const MIN_WIDTH = 400;
const MAX_WIDTH = 800;

const TypewriterMessage = ({ content = '', onComplete, onScroll, isNew }) => {
  const [displayedContent, setDisplayedContent] = useState(isNew ? '' : content);
  const [currentIndex, setCurrentIndex] = useState(isNew ? 0 : content.length);

  useEffect(() => {
    if (!isNew || !content) return;

    if (currentIndex < content.length) {
      const timer = setTimeout(() => {
        setDisplayedContent(prev => prev + content[currentIndex]);
        setCurrentIndex(currentIndex + 1);
        onScroll?.();
      }, 15);

      return () => clearTimeout(timer);
    } else if (onComplete) {
      onComplete();
      onScroll?.();
    }
  }, [currentIndex, content, onComplete, onScroll, isNew]);

  return <MarkdownRenderer content={displayedContent || ''} />;
};

const AIChatSidebar = ({
  isOpen,
  onClose,
  noteTitle = '',
  noteContent = '',
  noteId,
  width,
  onResize,
}) => {
  const { updateNote } = useNotes();
  const [messages, setMessages] = useState([]);
  const [newMessageId, setNewMessageId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const { showError: showAIError } = useAIError();
  const [isResizing, setIsResizing] = useState(false);
  const messagesEndRef = useRef(null);
  const startXRef = useRef(0);
  const startWidthRef = useRef(0);
  const { showError } = useError();
  const [error, setError] = useState(null);
  const inputRef = useRef(null);
  const [userInput, setUserInput] = useState('');
  const isMobile = useMediaQuery({ maxWidth: 768 });

  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  useEffect(() => {
    // Load existing chat messages if any
    if (noteId && isOpen) {
      const existingMessages = localStorage.getItem(`chat_${noteId}`);
      if (existingMessages) {
        setMessages(JSON.parse(existingMessages));
      }
    }
  }, [noteId, isOpen]);

  const handleResizeStart = useCallback((e) => {
    e.preventDefault();
    setIsResizing(true);
    startXRef.current = e.clientX;
    startWidthRef.current = width;
    document.body.style.cursor = "col-resize";
  }, [width]);

  useEffect(() => {
    const handleResize = (e) => {
      if (!isResizing) return;
      const diff = startXRef.current - e.clientX;
      const newWidth = Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, startWidthRef.current + diff));
      onResize(newWidth);
    };

    const handleResizeEnd = () => {
      setIsResizing(false);
      document.body.style.cursor = "";
    };

    if (isResizing) {
      document.addEventListener("mousemove", handleResize);
      document.addEventListener("mouseup", handleResizeEnd);
    }

    return () => {
      document.removeEventListener("mousemove", handleResize);
      document.removeEventListener("mouseup", handleResizeEnd);
      document.body.style.cursor = "";
    };
  }, [isResizing, onResize]);

  const handleSubmit = async (e) => {
    if (e) {
      e.preventDefault();
    }
    
    if (!userInput.trim()) return;

    // Add user message
    setMessages((prevMessages) => [
      ...prevMessages,
      { role: 'user', content: userInput },
    ]);

    // Clear input
    setUserInput('');
    setIsLoading(true);
    scrollToBottom();

    try {
      console.log('Sending chat request...');
      
      const response = await apiService.chat([
        {
          role: 'system',
          content: `You are a helpful AI assistant. You have access to the content of a note "${noteContent}". 
                   discuss it if asked in any way. Keep responses brief and direct (2-3 sentences).`
        },
        { role: 'user', content: userInput }
      ], noteTitle, noteContent);
      
      console.log('Chat response received:', response);

      // Also fetch current usage after chat
      const profileData = await apiService.get('/api/profile');
      console.log('Current usage after chat:', profileData.usage);
      
      const aiMessage = {
        role: 'assistant',
        content: response.content || response.summary || '',
        id: Date.now()
      };

      setMessages(prev => [...prev, aiMessage]);
      setNewMessageId(aiMessage.id);

      // Save chat history
      if (noteId) {
        const updatedMessages = [...messages, { role: 'user', content: userInput }, aiMessage];
        localStorage.setItem(`chat_${noteId}`, JSON.stringify(updatedMessages));
      }
    } catch (error) {
      console.error('Chat error:', error);
      showError('Failed to get AI response');
      setError('AI service is temporarily unavailable');
    } finally {
      setIsLoading(false);
      scrollToBottom();
      inputRef.current?.focus();
    }
  };

  const handleClearChat = async () => {
    setMessages([]);
    setError(null);
    if (noteId) {
      localStorage.removeItem(`chat_${noteId}`);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  if (!isOpen) return null;

  return (
    <div 
      className={`h-full flex flex-col w-full fixed top-0 right-0 bottom-0 bg-dark-card border-l border-gray-800/40 z-20 transition-transform duration-300 ease-in-out ${
        isMobile ? 'translate-x-0' : ''
      }`}
      style={{ 
        width: isMobile ? '100%' : `${width}px`,
        transform: isMobile && !isOpen ? 'translateX(100%)' : 'translateX(0)',
      }}
    >
      {/* Resize Handle */}
      {!isMobile && (
        <div
          className="absolute left-0 top-0 bottom-0 w-1 cursor-col-resize hover:bg-blue-500/20 transition-colors"
          onMouseDown={handleResizeStart}
        />
      )}

      {/* Header */}
      <div className="p-4 border-b border-gray-800/40 flex items-center justify-between">
        <h2 className="text-xl font-medium text-white">AI Assistant</h2>
        <div className="flex items-center space-x-2">
          <button
            onClick={handleClearChat}
            className="text-gray-400 hover:text-red-400 transition-colors p-1.5 hover:bg-red-500/10 rounded"
            title="Clear chat history"
          >
            <TrashIcon className="w-5 h-5" />
          </button>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white transition-colors"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>
      </div>

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {error ? (
          <ErrorState 
            title="AI Service Error"
            message={error}
            action={() => setError(null)}
            actionText="Dismiss"
          />
        ) : messages.length === 0 ? (
          <div className="text-center text-gray-400 text-base py-4">
            Ask me anything about your note!
          </div>
        ) : (
          messages.map((message, index) => (
            <div
              key={index}
              className={`flex ${
                message.role === "user" ? "justify-end" : "justify-start"
              }`}
            >
              <div
                className={`max-w-[90%] rounded-lg px-4 py-2 ${
                  message.role === "user"
                    ? "bg-blue-500 text-white"
                    : "bg-dark-hover text-gray-200"
                }`}
              >
                <TypewriterMessage
                  content={message.content}
                  onScroll={scrollToBottom}
                  isNew={message.id === newMessageId}
                />
              </div>
            </div>
          ))
        )}
        {isLoading && (
          <div className="flex justify-start">
            <div className="bg-dark-hover rounded-lg px-4 py-2">
              <AILoadingIndicator />
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <form onSubmit={handleSubmit} className="p-4 border-t border-gray-800/40">
        <div className="flex items-center space-x-3">
          <textarea
            ref={inputRef}
            value={userInput}
            onChange={(e) => setUserInput(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Ask me anything..."
            rows={1}
            className="flex-1 bg-dark-bg text-gray-200 rounded-lg px-4 py-2 text-base border-0 focus:outline-none focus:ring-1 focus:ring-gray-700 resize-none"
            style={{
              minHeight: '42px',
              maxHeight: '120px'
            }}
          />
          <button
            type="submit"
            disabled={!userInput.trim() || isLoading}
            className={`p-2 rounded-lg transition-colors ${
              !userInput.trim() || isLoading
                ? "text-gray-600 cursor-not-allowed"
                : "text-blue-500 hover:text-blue-400 hover:bg-blue-500/10"
            }`}
          >
            <PaperAirplaneIcon className="w-5 h-5" />
          </button>
        </div>
      </form>
    </div>
  );
};

export default AIChatSidebar;
